import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { addDocument, getCollection, updateDocument, deleteDocument } from '../../services/firestoreService';

const PRODUCT_TYPES = [
  { value: 'INSUMO', label: 'Insumo' },
  { value: 'FARMACO', label: 'Fármaco' },
  { value: 'SERVICIO', label: 'Servicio' }
];

const ManageProducts = () => {
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);

  const [code, setCode] = useState('');
  const [barcode, setBarcode] = useState('');
  const [name, setName] = useState('');
  const [productType, setProductType] = useState('INSUMO');
  const [isStockable, setIsStockable] = useState(true);
  const [isControlledSubstance, setIsControlledSubstance] = useState(false);
  const [requiresLotTracking, setRequiresLotTracking] = useState(false);
  const [requiresPrescription, setRequiresPrescription] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const [baseUomId, setBaseUomId] = useState('');
  const [purchaseUomId, setPurchaseUomId] = useState('');
  const [purchaseFactorToBase, setPurchaseFactorToBase] = useState('1');
  const [saleUomId, setSaleUomId] = useState('');
  const [saleFactorToBase, setSaleFactorToBase] = useState('1');

  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchProducts();
    fetchUnits();
  }, []);

  const fetchProducts = async () => {
    try {
      const data = await getCollection('products');
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar productos',
        severity: 'error'
      });
    }
  };

  const fetchUnits = async () => {
    try {
      const data = await getCollection('units');
      setUnits(data.filter((u) => u.isActive !== false));
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const findUnitById = (id) => units.find((u) => u.id === id) || null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!code.trim() || !name.trim()) {
      setSnackbar({ open: true, message: 'Código y nombre son obligatorios', severity: 'error' });
      return;
    }

    if (!baseUomId) {
      setSnackbar({ open: true, message: 'Debe seleccionar una unidad base', severity: 'error' });
      return;
    }

    const parsedPurchaseFactor = parseFloat(purchaseFactorToBase || '1');
    const parsedSaleFactor = parseFloat(saleFactorToBase || '1');

    if (isNaN(parsedPurchaseFactor) || parsedPurchaseFactor <= 0) {
      setSnackbar({ open: true, message: 'El factor de conversión de compra debe ser mayor que 0', severity: 'error' });
      return;
    }

    if (isNaN(parsedSaleFactor) || parsedSaleFactor <= 0) {
      setSnackbar({ open: true, message: 'El factor de conversión de venta debe ser mayor que 0', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const now = new Date();
      const baseUnit = findUnitById(baseUomId);
      const purchaseUnit = purchaseUomId ? findUnitById(purchaseUomId) : null;
      const saleUnit = saleUomId ? findUnitById(saleUomId) : null;

      const productData = {
        code: code.trim().toUpperCase(),
        barcode: barcode.trim() || null,
        name: name.trim(),
        productTypeCode: productType,
        isStockable,
        isControlledSubstance,
        requiresLotTracking,
        requiresPrescription,
        isActive,

        baseUomId,
        baseUomCode: baseUnit?.code || null,

        purchaseUomId: purchaseUomId || null,
        purchaseUomCode: purchaseUnit?.code || null,
        purchaseFactorToBase: purchaseUomId ? parsedPurchaseFactor : 1,

        saleUomId: saleUomId || null,
        saleUomCode: saleUnit?.code || null,
        saleFactorToBase: saleUomId ? parsedSaleFactor : 1,

        createdAt: editingProduct ? editingProduct.createdAt || now : now,
        updatedAt: now
      };

      if (editingProduct) {
        await updateDocument('products', editingProduct.id, productData);
        setSnackbar({ open: true, message: 'Producto actualizado exitosamente', severity: 'success' });
      } else {
        await addDocument('products', productData);
        setSnackbar({ open: true, message: 'Producto creado exitosamente', severity: 'success' });
      }

      handleCancelEdit();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      setSnackbar({ open: true, message: 'Error al guardar el producto', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setCode(product.code || '');
    setBarcode(product.barcode || '');
    setName(product.name || '');
    setProductType(product.productTypeCode || 'INSUMO');
    setIsStockable(product.isStockable !== undefined ? product.isStockable : true);
    setIsControlledSubstance(!!product.isControlledSubstance);
    setRequiresLotTracking(!!product.requiresLotTracking);
    setRequiresPrescription(!!product.requiresPrescription);
    setIsActive(product.isActive !== undefined ? product.isActive : true);

    setBaseUomId(product.baseUomId || '');
    setPurchaseUomId(product.purchaseUomId || '');
    setPurchaseFactorToBase((product.purchaseFactorToBase || 1).toString());
    setSaleUomId(product.saleUomId || '');
    setSaleFactorToBase((product.saleFactorToBase || 1).toString());
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setCode('');
    setBarcode('');
    setName('');
    setProductType('INSUMO');
    setIsStockable(true);
    setIsControlledSubstance(false);
    setRequiresLotTracking(false);
    setRequiresPrescription(false);
    setIsActive(true);

    setBaseUomId('');
    setPurchaseUomId('');
    setPurchaseFactorToBase('1');
    setSaleUomId('');
    setSaleFactorToBase('1');
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;

    try {
      await deleteDocument('products', productId);
      setSnackbar({ open: true, message: 'Producto eliminado exitosamente', severity: 'success' });
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      setSnackbar({ open: true, message: 'Error al eliminar el producto', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getProductTypeLabel = (value) => {
    const found = PRODUCT_TYPES.find((t) => t.value === value);
    return found ? found.label : value;
  };

  const getUnitCode = (id) => {
    const u = findUnitById(id);
    return u ? u.code : '';
  };

  const getUomTypeLabel = (value) => {
    const map = {
      UNIDAD: 'Unidad',
      VOLUMEN: 'Volumen',
      MASA: 'Masa',
      EMPAQUE: 'Empaque'
    };
    return map[value] || value || '';
  };

  const formatUnitLabel = (unit) => {
    if (!unit) return '';
    const typeLabel = getUomTypeLabel(unit.uomType);
    return `${unit.code} - ${unit.name}${typeLabel ? ` (${typeLabel})` : ''}`;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Catálogo de Productos
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Define los insumos, fármacos y servicios, junto con sus unidades base, de compra y de venta.
      </Typography>
      <Paper sx={{ p: 3, mt: 2 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 3
              }}>
              <TextField
                fullWidth
                label="Código"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                placeholder="Ej: AMOX500"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 3
              }}>
              <TextField
                fullWidth
                label="Código de Barra"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder="Escanear o ingresar código de barra"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Ej: Amoxicilina 500 mg tableta"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4
              }}>
              <TextField
                select
                fullWidth
                label="Tipo de Producto"
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                required
              >
                {PRODUCT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 3
              }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isStockable}
                    onChange={(e) => setIsStockable(e.target.checked)}
                    color="primary"
                  />
                }
                label="Maneja stock"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 3
              }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isControlledSubstance}
                    onChange={(e) => setIsControlledSubstance(e.target.checked)}
                    color="primary"
                  />
                }
                label="Controlado / Estupefaciente"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 3
              }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={requiresLotTracking}
                    onChange={(e) => setRequiresLotTracking(e.target.checked)}
                    color="primary"
                  />
                }
                label="Requiere lote/vencimiento"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 3
              }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={requiresPrescription}
                    onChange={(e) => setRequiresPrescription(e.target.checked)}
                    color="primary"
                  />
                }
                label="Requiere receta"
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 4
              }}>
              <TextField
                select
                fullWidth
                label="Unidad Base"
                value={baseUomId}
                onChange={(e) => setBaseUomId(e.target.value)}
                required
                helperText="Unidad en la que se almacena el stock internamente"
              >
                {units.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {formatUnitLabel(u)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 4
              }}>
              <TextField
                select
                fullWidth
                label="Unidad de Compra"
                value={purchaseUomId}
                onChange={(e) => setPurchaseUomId(e.target.value)}
                helperText="Opcional: unidad en que se compra (ej: CAJA_100)"
              >
                <MenuItem value="">Sin unidad de compra</MenuItem>
                {units.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {formatUnitLabel(u)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4
              }}>
              <TextField
                fullWidth
                label="Factor compra → base"
                value={purchaseFactorToBase}
                onChange={(e) => setPurchaseFactorToBase(e.target.value)}
                helperText="Ej: 100 si 1 caja = 100 unidades base"
                type="number"
                inputProps={{ min: 0, step: '0.0001' }}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 4
              }}>
              <TextField
                select
                fullWidth
                label="Unidad de Venta"
                value={saleUomId}
                onChange={(e) => setSaleUomId(e.target.value)}
                helperText="Opcional: unidad en que se factura (ej: TABLETA)"
              >
                <MenuItem value="">Sin unidad de venta específica</MenuItem>
                {units.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {formatUnitLabel(u)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4
              }}>
              <TextField
                fullWidth
                label="Factor venta → base"
                value={saleFactorToBase}
                onChange={(e) => setSaleFactorToBase(e.target.value)}
                helperText="Ej: 1 si se vende en la misma unidad base"
                type="number"
                inputProps={{ min: 0, step: '0.0001' }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4
              }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Producto activo"
              />
            </Grid>

            <Grid size={12}>
              <Button type="submit" variant="contained" disabled={loading} sx={{ mr: 1 }}>
                {loading ? 'Guardando...' : editingProduct ? 'Actualizar Producto' : 'Crear Producto'}
              </Button>
              {editingProduct && (
                <Button variant="outlined" onClick={handleCancelEdit}>
                  Cancelar
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Productos configurados
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Código de Barra</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Unidad Base</TableCell>
                <TableCell>Maneja Stock</TableCell>
                <TableCell>Activo</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.code}</TableCell>
                  <TableCell>{p.barcode || '-'}</TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{getProductTypeLabel(p.productTypeCode)}</TableCell>
                  <TableCell>{p.baseUomCode || getUnitCode(p.baseUomId)}</TableCell>
                  <TableCell>{p.isStockable ? 'Sí' : 'No'}</TableCell>
                  <TableCell>{p.isActive ? 'Sí' : 'No'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(p)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(p.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {products.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No hay productos configurados todavía.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageProducts;
